/**
 * Envío PJ-1 · panel "Personajes" (panel contextual de la Vista Edición).
 *
 * Arriba, el REPARTO del proyecto (los mismos personajes que lee LMX, tabla
 * characters vía /api/characters). Al tocar uno se edita su ficha: foto,
 * apariencia, personalidad, forma de hablar, voz e imágenes de referencia.
 * Lo que lleva generación (hoja de modelo) se le pide a LMX.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, Plus, Trash2, Upload, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { useCharacterStore } from '../../store/characterStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { uploadToR2 } from '../../utils/uploadToR2';
import VoicePreviewButton from '../nodes/VoicePreviewButton';
import { fieldsPatch, linesToList, listToText, textToList, withVoice } from '../../store/characterDraft';
import { ELEVEN_VOICES as VOICES } from '../../utils/elevenVoices';

const KINDS = [['', 'Sin definir'], ['persona', 'Persona'], ['animal', 'Animal'],
  ['criatura', 'Criatura'], ['mascota', 'Mascota de marca'], ['objeto', 'Objeto animado']];
const REF_ROLES = [['face_front', 'Frente'], ['face_34', '3/4'], ['profile', 'Perfil'],
  ['full_body', 'Cuerpo entero'], ['expression', 'Expresión'], ['look', 'Look'], ['style', 'Estilo']];
const ROLE_LABEL = Object.fromEntries(REF_ROLES);

const Avatar = ({ url, name, size = 34 }) => (
  <span className="tl-char-avatar" style={{ width: size, height: size }}>
    {url ? <img src={url} alt="" /> : <span>{(name || '?').slice(0, 1)}</span>}
  </span>
);

const Field = ({ label, children, hint }) => (
  <label className="tl-inspector-field wide"><span>{label}{hint && <em> {hint}</em>}</span>{children}</label>
);

// Envío PJ-2 · exportado: la Biblioteca usa el mismo editor.
export const CharacterEditor = ({ projectId, onBack, backLabel = 'Reparto', canRemove = true }) => {
  const { current, update, undo, removeFromCast } = useCharacterStore();
  const ch = current?.character;
  const card = current?.card;
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('invented');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refRole, setRefRole] = useState('face_front');

  useEffect(() => {
    setDraft(ch ? JSON.parse(JSON.stringify(ch.fields || {})) : null);
    setName(ch?.name || '');
    setOrigin(ch?.origin || 'invented');
    setConsent(!!ch?.consent_at);
  }, [ch]);

  const patch = useMemo(() => (ch && draft ? fieldsPatch(ch.fields || {}, draft) : {}), [ch, draft]);
  const dirty = ch && draft && (Object.keys(patch).length > 0 || name.trim() !== ch.name
    || origin !== (ch.origin || 'invented') || consent !== !!ch.consent_at);

  if (!ch || !draft) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setFace = (k, v) => setDraft((d) => ({ ...d, face: { ...(d.face || {}), [k]: v } }));
  const voice = (draft.voices || [])[0];
  const refs = card?.references || [];

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo completar');
    } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const body = { fields: patch };
    if (name.trim() && name.trim() !== ch.name) body.name = name.trim();
    if (origin !== (ch.origin || 'invented')) body.origin = origin;
    if (origin === 'real' && consent !== !!ch.consent_at) body.consent = consent;
    await update(body);
  }, 'Personaje guardado. LMX ya usa estos datos.');

  const upload = async (file) => {
    const res = await uploadToR2(file);
    if (!res.assetId) throw new Error('sin asset');
    return res.assetId;
  };
  const uploadCover = (file) => file && run(async () => {
    await update({ cover_asset_id: await upload(file) });
  }, 'Foto actualizada');
  const addRef = (file) => file && run(async () => {
    const assetId = await upload(file);
    const current = (ch.fields?.references || []);
    await update({ fields: { references: [...current, { asset_id: assetId, role: refRole }] } });
  }, `Referencia «${ROLE_LABEL[refRole]}» agregada`);
  const removeRef = (assetId) => run(async () => {
    const left = (ch.fields?.references || []).filter((r) => r.asset_id !== assetId);
    await update({ fields: { references: left } });
  }, 'Referencia quitada');

  return (
    <div className="tl-brand-body tl-char-body" data-testid="tl-char-editor">
      <button type="button" className="tl-brand-link tl-char-back" onClick={onBack} data-testid="tl-char-back">
        <ArrowLeft size={13} /> {backLabel}
      </button>

      <div className="tl-char-head">
        <Avatar url={card?.cover_url} name={ch.name} size={64} />
        <div>
          <b>{ch.name}</b>
          <code>@{ch.handle}</code>
          <label className="tl-brand-btn tl-char-upload">
            <Upload size={13} /> {card?.cover_url ? 'Cambiar foto' : 'Subir foto'}
            <input type="file" accept="image/*" hidden onChange={(e) => { uploadCover(e.target.files?.[0]); e.target.value = ''; }} data-testid="tl-char-cover-input" />
          </label>
        </div>
      </div>

      {card?.needs_consent && (
        <p className="tl-char-warn" data-testid="tl-char-consent-warn">
          Es una persona real: sin su autorización no se usa en ninguna generación.
        </p>
      )}

      <Field label="Nombre"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="tl-char-name" /></Field>
      <div className="tl-char-row">
        <Field label="Tipo">
          <select value={draft.kind || ''} onChange={(e) => set('kind', e.target.value)} data-testid="tl-char-kind">
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Rol"><input value={draft.role || ''} placeholder="protagonista, presentador…" onChange={(e) => set('role', e.target.value)} /></Field>
      </div>
      <div className="tl-char-origin" role="radiogroup" aria-label="Origen">
        {[['invented', 'Inventado'], ['real', 'Persona real']].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={origin === v}
            className={origin === v ? 'active' : ''} onClick={() => setOrigin(v)} data-testid={`tl-char-origin-${v}`}>{l}</button>
        ))}
      </div>
      {origin === 'real' && (
        <label className="tl-char-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="tl-char-consent" />
          Tengo autorización de esta persona para usar su imagen y su voz.
        </label>
      )}

      <div className="tl-inspector-subhead">Apariencia</div>
      <Field label="Descripción" hint="(1 a 3 frases, lo que lo hace reconocible)">
        <textarea rows={3} value={draft.descriptor || ''} onChange={(e) => set('descriptor', e.target.value)} data-testid="tl-char-descriptor" />
      </Field>
      <Field label="Para los modelos" hint="(en inglés; si falta, LMX la escribe)">
        <textarea rows={2} value={draft.descriptor_en || ''} onChange={(e) => set('descriptor_en', e.target.value)} />
      </Field>
      <div className="tl-char-row">
        <Field label="Pelo"><input value={draft.face?.hair || ''} onChange={(e) => setFace('hair', e.target.value)} /></Field>
        <Field label="Ojos"><input value={draft.face?.eyes || ''} onChange={(e) => setFace('eyes', e.target.value)} /></Field>
      </div>
      <Field label="Rasgos distintivos" hint="(separados por coma)">
        <input value={listToText(draft.face?.distinctive)} onChange={(e) => setFace('distinctive', textToList(e.target.value))} />
      </Field>
      <Field label="Estilo visual"><input value={draft.visual_style || ''} placeholder="3D estilizado, fotorrealista, anime…" onChange={(e) => set('visual_style', e.target.value)} /></Field>

      <div className="tl-inspector-subhead">Personalidad y forma de hablar</div>
      <Field label="Rasgos" hint="(separados por coma)">
        <input value={listToText(draft.traits)} onChange={(e) => set('traits', textToList(e.target.value))} />
      </Field>
      <div className="tl-char-row">
        <Field label="Acento"><input value={draft.accent || ''} placeholder="rioplatense" onChange={(e) => set('accent', e.target.value)} /></Field>
        <Field label="Registro"><input value={draft.register || ''} placeholder="cálido, informal" onChange={(e) => set('register', e.target.value)} /></Field>
      </div>
      <Field label="Frases típicas" hint="(una por línea)">
        <textarea rows={2} value={(draft.catchphrases || []).join('\n')} onChange={(e) => set('catchphrases', linesToList(e.target.value))} />
      </Field>
      <Field label="Gestos" hint="(separados por coma)">
        <input value={listToText(draft.gestures)} onChange={(e) => set('gestures', textToList(e.target.value))} />
      </Field>

      <div className="tl-inspector-subhead">Voz <em>(la usa en todos los nodos de voz)</em></div>
      <div className="tl-char-voice">
        <select value={voice?.voice || ''} onChange={(e) => set('voices', e.target.value ? withVoice(draft.voices, { voice: e.target.value, provider: 'elevenlabs' }) : [])} data-testid="tl-char-voice">
          <option value="">Sin voz</option>
          {voice?.voice && !VOICES.includes(voice.voice) && <option value={voice.voice}>{voice.voice}</option>}
          {VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        {voice?.voice && VOICES.includes(voice.voice) && <VoicePreviewButton voice={voice.voice} testId="tl-char-voice-preview" />}
      </div>
      {voice?.voice && (
        <div className="tl-char-row">
          <Field label={`Velocidad ${(voice.speed ?? 1).toFixed(2)}`}>
            <input type="range" min="0.7" max="1.2" step="0.05" value={voice.speed ?? 1}
              onChange={(e) => set('voices', withVoice(draft.voices, { speed: Number(e.target.value) }))} />
          </Field>
          <Field label={`Estabilidad ${(voice.stability ?? 0.5).toFixed(2)}`}>
            <input type="range" min="0" max="1" step="0.05" value={voice.stability ?? 0.5}
              onChange={(e) => set('voices', withVoice(draft.voices, { stability: Number(e.target.value) }))} />
          </Field>
        </div>
      )}

      <div className="tl-brand-actions">
        <button type="button" className="tl-brand-save tl-char-save" disabled={!dirty || busy} onClick={save} data-testid="tl-char-save">
          {busy ? <Loader2 size={13} className="spin" /> : null} Guardar
        </button>
        <button type="button" className="tl-brand-link" disabled={busy || dirty}
          title={dirty ? 'Guardá o descartá los cambios antes de deshacer' : 'Volver a como estaba antes del último cambio'}
          onClick={() => run(() => undo(), 'Personaje restaurado a la versión anterior')} data-testid="tl-char-undo">
          Deshacer último cambio
        </button>
      </div>

      <div className="tl-inspector-subhead">Imágenes de referencia <em>(LMX elige cuáles usar según el plano)</em></div>
      <div className="tl-char-refs" data-testid="tl-char-refs">
        {refs.map((r) => (
          <figure key={r.asset_id} className={r.missing ? 'is-missing' : ''}>
            {r.missing ? <span>Falta la imagen</span> : <img src={r.url} alt={r.label} loading="lazy" />}
            <figcaption>{ROLE_LABEL[r.role] || r.label}</figcaption>
            <button type="button" className="tl-icon-btn" onClick={() => removeRef(r.asset_id)} disabled={busy} aria-label="Quitar referencia">
              <Trash2 size={12} />
            </button>
          </figure>
        ))}
      </div>
      <div className="tl-brand-new">
        <select value={refRole} onChange={(e) => setRefRole(e.target.value)} aria-label="Qué muestra la imagen" data-testid="tl-char-ref-role">
          {REF_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="tl-brand-btn">
          <Upload size={13} /> Subir
          <input type="file" accept="image/*" hidden onChange={(e) => { addRef(e.target.files?.[0]); e.target.value = ''; }} data-testid="tl-char-ref-input" />
        </label>
      </div>
      {!!card?.missing_sheet?.length && (
        <p className="tl-brand-hint">
          Faltan {card.missing_sheet.join(', ')}. Pedile a LMX «generá la hoja de modelo de @{ch.handle}»: la arma desde la foto y te muestra el costo antes.
        </p>
      )}

      {projectId && canRemove && (
        <button type="button" className="tl-brand-link" disabled={busy}
          onClick={() => run(async () => { await removeFromCast(projectId, ch.id); onBack(); }, `@${ch.handle} salió del reparto`)}
          data-testid="tl-char-remove">
          Quitar del reparto de este proyecto
        </button>
      )}
    </div>
  );
};

const CharacterPanel = () => {
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const { cast, characters, current, loading, loadCast, loadList, open, close, addToCast, create } = useCharacterStore();
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => { if (projectId) { loadCast(projectId, { force: true }); close(); } }, [projectId, loadCast, close]);
  useEffect(() => { loadList(); }, [loadList]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo completar');
    } finally { setBusy(false); }
  };

  if (!projectId) {
    return (
      <aside className="tl-inspector tl-brand-panel tl-char-panel" data-testid="tl-char-panel">
        <div className="tl-inspector-header"><span className="tl-inspector-title">Personajes</span></div>
        <p className="tl-brand-empty">Guardá el proyecto para armar su reparto.</p>
      </aside>
    );
  }

  const inCast = new Set(cast.map((c) => c.id));
  const others = (characters || []).filter((c) => !inCast.has(c.id));

  return (
    <aside className="tl-inspector tl-brand-panel tl-char-panel" data-testid="tl-char-panel" aria-label="Personajes del proyecto">
      <div className="tl-inspector-header">
        <span className="tl-inspector-title">
          {current ? `Personaje · @${current.character.handle}` : `Personajes${cast.length ? ` · reparto (${cast.length})` : ''}`}
        </span>
      </div>

      {current ? <CharacterEditor projectId={projectId} onBack={close} /> : (
        <div className="tl-brand-pick">
          {loading && <div className="tl-brand-empty"><Loader2 size={16} className="spin" /> Cargando…</div>}
          {!loading && cast.length === 0 && (
            <p className="tl-brand-empty">Este proyecto no tiene reparto. Sumá un personaje tuyo o creá uno nuevo.</p>
          )}
          {cast.map((c) => (
            <button key={c.id} type="button" className="tl-brand-option tl-char-option" disabled={busy}
              onClick={() => run(() => open(c.id))} data-testid="tl-char-cast-item">
              <Avatar url={c.cover_url} name={c.name} />
              <span className="tl-char-option-txt">
                <b>{c.name}</b>
                <small>@{c.handle}{c.project_role ? ` · ${c.project_role}` : ''}{c.needs_consent ? ' · falta autorización' : ''}</small>
              </span>
              {!c.has_voice && <em className="tl-char-tag">sin voz</em>}
            </button>
          ))}

          {others.length > 0 && <div className="tl-inspector-subhead">Sumar al reparto</div>}
          {others.map((c) => (
            <button key={c.id} type="button" className="tl-brand-option tl-char-option is-other" disabled={busy}
              onClick={() => run(() => addToCast(projectId, c.id), `@${c.handle} sumado al reparto`)} data-testid="tl-char-add">
              <Avatar url={c.cover_url} name={c.name} size={26} />
              <span className="tl-char-option-txt"><b>{c.name}</b><small>@{c.handle}</small></span>
              <Plus size={14} />
            </button>
          ))}

          <div className="tl-brand-new">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre de un personaje nuevo" data-testid="tl-char-new-name" />
            <button type="button" disabled={busy || !newName.trim()}
              onClick={() => run(async () => { await create(projectId, { name: newName.trim() }); setNewName(''); }, 'Personaje creado y sumado al reparto')}
              data-testid="tl-char-create">
              <UserRound size={13} /> Crear
            </button>
          </div>
          <p className="tl-brand-hint">Más rápido con LMX: adjuntale una foto (📎) y pedile «creá un personaje con esta foto». Arma la ficha, la hoja de modelo y la voz.</p>
        </div>
      )}
    </aside>
  );
};

export default CharacterPanel;
